/**
 * Fetch and normalize media folder manifest.json: public CDN first, then presigned GET via
 * media-handle `uploadImages` if needed.
 * @see ../config/mediaManifest.schema.json
 */

import axios from "axios";
import {
  CDN_IMAGE_BASE,
  MANIFEST_FILE,
  ITEMS_SHARED_GALLERY_ITEM_ID,
  parseMediaFolderPrefix,
  cdnSafeMediaPathSegment,
  mediaPath,
} from "../config/mediaPaths";
import {
  operationTypeForManifestRead,
  operationTypeForGalleryUpload,
} from "../config/mediaOperationTypes";
import { handleMedia } from "../config/api";
import { Encrypt } from "./encryption";
import { putPresignedObject } from "./putPresignedObject";

/**
 * @param {unknown} data
 * @returns {{ version: number, images: Array<{ imageKey: string, originalFilename: string, tags: string[] }> }}
 */
/**
 * PUT updated manifest JSON to S3 using the presigned URL from media-handle `uploadImages` (`manifestPresignedUrl`).
 * @param {string} presignedUrl
 * @param {{ version: number, images: unknown[] }} manifest
 * @param {null} [_manifestCrypto] — reserved; manifests are always plaintext JSON.
 * @param {object} [_options] — reserved.
 */
export async function putManifestWithPresignedUrl(presignedUrl, manifest, _manifestCrypto = null, _options = {}) {
  if (!presignedUrl || typeof presignedUrl !== "string") {
    throw new Error("Missing manifest presigned URL");
  }
  const body = JSON.stringify(manifest);
  try {
    await putPresignedObject(presignedUrl, body, "application/json");
  } catch (err) {
    const statusMatch = String(err?.message || "").match(/Upload failed: (\d+)/);
    if (statusMatch) {
      throw new Error(`Manifest upload failed: ${statusMatch[1]}`);
    }
    throw err;
  }
}

export function normalizeManifest(data) {
  if (!data || typeof data !== "object") {
    return { version: 1, images: [] };
  }
  const images = Array.isArray(data.images)
    ? data.images
        .map((entry) => {
          if (!entry || typeof entry !== "object") return null;
          const imageKey = entry.imageKey != null ? String(entry.imageKey) : "";
          const originalFilename =
            entry.originalFilename != null
              ? String(entry.originalFilename)
              : entry.originalName != null
                ? String(entry.originalName)
                : "";
          const tags = Array.isArray(entry.tags)
            ? entry.tags.map((t) => String(t).toLowerCase())
            : [];
          const linkedItemIds = Array.isArray(entry.linkedItemIds)
            ? entry.linkedItemIds.map((id) => String(id)).filter(Boolean)
            : [];
          return imageKey
            ? { imageKey, originalFilename, tags, ...(linkedItemIds.length ? { linkedItemIds } : {}) }
            : null;
        })
        .filter(Boolean)
    : [];
  return {
    version: typeof data.version === "number" ? data.version : 1,
    images,
  };
}

/**
 * After a manifest PUT, CDN may still serve an older JSON briefly. When re-fetching, compare with
 * Redux/local state and avoid regressing version or image row count.
 * @param {unknown} localManifest
 * @param {unknown} remoteManifest
 * @returns {{ version: number, images: unknown[] }}
 */
export function preferNewerOrRicherManifest(localManifest, remoteManifest) {
  const local = normalizeManifest(localManifest);
  const remote = normalizeManifest(remoteManifest);
  if (remote.version > local.version) return remote;
  if (remote.version < local.version) return local;
  const localLen = local.images.length;
  const remoteLen = remote.images.length;
  if (remoteLen > localLen) return remote;
  if (localLen > remoteLen) return local;
  return remote;
}

/**
 * Shared itemData for manifest-only presign (read or write), same path shape as gallery uploads.
 * @param {{ hubId: string, bizId: string | null, gallerySegment: 'hub' | 'biz' | 'items' }} parsed
 */
function baseManifestItemData(parsed) {
  const { hubId, bizId, gallerySegment } = parsed;
  const itemId =
    gallerySegment === "hub"
      ? "hub-gallery"
      : gallerySegment === "biz"
        ? "biz-gallery"
        : ITEMS_SHARED_GALLERY_ITEM_ID;
  const manifestTags =
    gallerySegment === "hub"
      ? ["hub"]
      : gallerySegment === "biz"
        ? ["biz"]
        : [];
  const itemData = {
    hubId,
    itemId,
    images: [{ fileName: MANIFEST_FILE }],
    gallerySegment,
    manifestTags,
  };
  if (gallerySegment !== "hub" && bizId) {
    itemData.storeId = cdnSafeMediaPathSegment(bizId);
  }
  return itemData;
}

/** GET presigned URL for manifest.json (read). */
function manifestReadItemData(parsed) {
  return {
    ...baseManifestItemData(parsed),
    operationType: operationTypeForManifestRead(parsed.gallerySegment),
  };
}

/** PUT presigned URL for manifest.json (after local merge, e.g. delete). */
function manifestWriteItemData(parsed) {
  return {
    ...baseManifestItemData(parsed),
    operationType: operationTypeForGalleryUpload(parsed.gallerySegment),
  };
}

/**
 * Request a presigned URL for manifest.json via the same action as image uploads.
 * @param {string} token
 * @param {string} pathPrefix
 * @returns {Promise<string | null>}
 */
async function requestManifestPresignedUrlViaUploadImages(token, pathPrefix) {
  const parsed = parseMediaFolderPrefix(pathPrefix);
  if (!parsed || !token) return null;
  try {
    const itemData = manifestReadItemData(parsed);
    const response = await axios.post(
      handleMedia,
      Encrypt({
        token,
        action: "uploadImages",
        itemData,
      }),
      { headers: { "Content-Type": "application/json" } }
    );
    const result = response.data;
    if (!result?.success) return null;
    const url =
      result.manifestPresignedUrl ||
      result.uploadedImages?.[0]?.presignedUrl;
    return typeof url === "string" && url.length > 0 ? url : null;
  } catch (e) {
    console.warn("manifest uploadImages presign failed", e?.message || e);
    return null;
  }
}

/**
 * Request a PUT presigned URL for manifest.json (same `uploadImages` action as image uploads).
 * @param {string} token
 * @param {string} pathPrefix
 * @returns {Promise<string | null>}
 */
export async function requestManifestPutPresignedUrl(token, pathPrefix) {
  const parsed = parseMediaFolderPrefix(pathPrefix);
  if (!parsed || !token) return null;
  try {
    const itemData = manifestWriteItemData(parsed);
    const response = await axios.post(
      handleMedia,
      Encrypt({
        token,
        action: "uploadImages",
        itemData,
      }),
      { headers: { "Content-Type": "application/json" } }
    );
    const result = response.data;
    if (!result?.success) return null;
    const url =
      result.manifestPresignedUrl ||
      result.uploadedImages?.[0]?.presignedUrl;
    return typeof url === "string" && url.length > 0 ? url : null;
  } catch (e) {
    console.warn("manifest PUT presign failed", e?.message || e);
    return null;
  }
}

function manifestUrlFromKey(key) {
  const parts = key.split("/").map((p) => encodeURIComponent(p));
  return `${CDN_IMAGE_BASE}/${parts.join("/")}`;
}

/** Coalesces concurrent GETs for the same storage prefix (many gallery consumers, same manifest). */
const manifestFetchInflight = new Map();

/**
 * Drop in-flight manifest GET for a path so the next `fetchManifestForPath` runs a fresh network read
 * (e.g. after upload or manifest PUT elsewhere).
 * @param {string} pathPrefix
 */
export function invalidateManifestFetchCacheForPath(pathPrefix) {
  const normalized = pathPrefix ? String(pathPrefix).replace(/\/$/, "") : "";
  if (!normalized) return;
  manifestFetchInflight.delete(normalized);
}

/**
 * GET manifest.json under a storage path prefix (MyHub/.../images folder).
 * Tries the public CDN URL first (deterministic; see `manifestUrlFromKey`).
 * If that yields no usable JSON (missing object, 403/404, parse error), falls back to a
 * presigned GET via media-handle `uploadImages` when `token` is set and the prefix parses.
 * @param {string} pathPrefix - full path without manifest filename
 * @param {{ token?: string, preferOrigin?: boolean }} [options]
 * @param {boolean} [options.preferOrigin] - If true, read via presigned S3 GET first (avoids stale CDN right after PUT); then CDN fallback.
 */
export async function fetchManifestForPath(pathPrefix, options = {}) {
  const { token, preferOrigin = false } = options;
  const normalized = pathPrefix ? pathPrefix.replace(/\/$/, "") : "";
  if (!normalized) {
    return { version: 1, images: [] };
  }

  const inflightKey = normalized;
  let inflight = manifestFetchInflight.get(inflightKey);
  if (!inflight) {
    inflight = (async () => {
      const key = `${normalized}/${MANIFEST_FILE}`;
      const url = manifestUrlFromKey(key);

      const parseResponse = async (res) => {
        if (!res.ok) return null;
        try {
          const data = await res.json();
          return normalizeManifest(data);
        } catch {
          return null;
        }
      };

      const fetchCdnManifest = async () => {
        try {
          const bustedUrl = `${url}${
            String(url).includes("?") ? "&" : "?"
          }cb=${Date.now()}`;
          const res = await fetch(bustedUrl, { cache: "no-store" });
          return parseResponse(res);
        } catch {
          return null;
        }
      };

      const fetchPresignedManifest = async () => {
        if (!token || !parseMediaFolderPrefix(normalized)) return null;
        const presigned = await requestManifestPresignedUrlViaUploadImages(
          token,
          normalized
        );
        if (!presigned) return null;
        try {
          const res = await fetch(presigned, { cache: "no-store" });
          return await parseResponse(res);
        } catch {
          return null;
        }
      };

      if (preferOrigin) {
        const originParsed = await fetchPresignedManifest();
        if (originParsed) return originParsed;
        const cdnAfterOrigin = await fetchCdnManifest();
        if (cdnAfterOrigin) return cdnAfterOrigin;
      } else {
        const cdnParsed = await fetchCdnManifest();
        if (cdnParsed) return cdnParsed;
        const presignedParsed = await fetchPresignedManifest();
        if (presignedParsed) return presignedParsed;
      }

      return { version: 1, images: [] };
    })();
    manifestFetchInflight.set(inflightKey, inflight);
    inflight.finally(() => {
      manifestFetchInflight.delete(inflightKey);
    });
  }

  return inflight;
}

function basenameOnlyForGalleryKey(key) {
  const k = String(key || "");
  const i = k.lastIndexOf("/");
  return i >= 0 ? k.slice(i + 1) : k;
}

/**
 * Map items-gallery image basename → item ids that reference it.
 * @param {unknown[]} products
 * @param {unknown[]} services
 * @returns {Map<string, Set<string>>}
 */
export function buildItemsGalleryLinkageMap(products = [], services = []) {
  const map = new Map();
  const add = (itemId, img) => {
    if (!itemId || !img || typeof img !== "object") return;
    const imageKey = img.imageKey;
    if (!imageKey) return;
    const bn = basenameOnlyForGalleryKey(imageKey);
    if (!bn) return;
    if (!map.has(bn)) map.set(bn, new Set());
    map.get(bn).add(String(itemId));
  };

  for (const product of products) {
    const id = product?.ItemId;
    if (!id) continue;
    const imgs = product.ItemImages ?? [];
    for (const img of imgs) {
      if (img && typeof img === "object") add(id, img);
    }
  }
  for (const service of services) {
    const id = service?.ItemId;
    if (!id) continue;
    const imgs = service.ItemImages ?? [];
    for (const img of imgs) {
      if (img && typeof img === "object") add(id, img);
    }
  }
  return map;
}

/**
 * Stable fingerprint for items-gallery linkage (for skipping manifest sync when unchanged).
 * Empty string when no items-gallery image refs.
 * @param {unknown[]} products
 * @param {unknown[]} services
 * @returns {string}
 */
export function itemsGalleryLinkageComparableKey(products = [], services = []) {
  const map = buildItemsGalleryLinkageMap(products, services);
  if (map.size === 0) return "";
  const entries = [...map.entries()]
    .map(([imageKey, idSet]) => [imageKey, [...idSet].sort()])
    .sort((entryA, entryB) => String(entryA[0]).localeCompare(String(entryB[0])));
  return JSON.stringify(entries);
}

/**
 * Rewrite optional `linkedItemIds` on items-gallery manifest rows from current product/service image refs.
 * Best-effort after biz save; does not block on failure.
 */
export async function syncItemsGalleryLinkedItemIds({
  token,
  hubId,
  bizId,
  products = [],
  services = [],
  manifestCrypto = null,
  bizPrivate = false,
}) {
  const pathPrefix = mediaPath({ entity: "item", fileType: "media", hubId, bizId });
  if (!pathPrefix || !token) return;

  const map = buildItemsGalleryLinkageMap(products, services);

  const manifest = await fetchManifestForPath(pathPrefix, { token, preferOrigin: true });
  const versioned = normalizeManifest(manifest);
  const nextImages = versioned.images.map((row) => {
    const ids = map.get(row.imageKey);
    const linkedItemIds = ids ? [...ids].sort() : [];
    const base = { ...row };
    if (linkedItemIds.length) base.linkedItemIds = linkedItemIds;
    else delete base.linkedItemIds;
    return base;
  });
  const nextManifest = {
    ...versioned,
    version: (versioned.version || 1) + 1,
    images: nextImages,
  };
  const putUrl = await requestManifestPutPresignedUrl(token, pathPrefix);
  if (!putUrl) return;
  await putManifestWithPresignedUrl(putUrl, nextManifest, null, {});
}
